import { useState, useEffect, useCallback, useRef } from 'react';
import sessionService from '../services/sessionService';

const HEARTBEAT_MS = 30 * 60 * 1000; // 30 minutes

export default function useLocationGuard(isAuthenticated) {
  const [isBlocked, setIsBlocked] = useState(false);
  const [locations, setLocations] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [limit, setLimit] = useState(4);
  const [blockMessage, setBlockMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const heartbeatRef = useRef(null);
  const mountedRef = useRef(true);

  const doCheckin = useCallback(async () => {
    try {
      const res = await sessionService.checkin();
      if (!mountedRef.current) return;

      setLocations(res.locations || []);
      setCurrentLocation(res.currentLocation || null);
      setLimit(res.limit || 4);

      if (res.allowed === false) {
        setIsBlocked(true);
        setBlockMessage(res.message || 'Location limit reached.');
      } else {
        setIsBlocked(false);
        setBlockMessage('');
      }
    } catch (err) {
      // Fail open: network errors should not block the user
      if (!mountedRef.current) return;
      setIsBlocked(false);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  const releaseLocation = useCallback(async (id) => {
    try {
      await sessionService.releaseLocation(id);
      setLocations((prev) => prev.filter((l) => l.id !== id));
      // Re-checkin to register current location now that a slot is free
      await doCheckin();
    } catch (err) {
      console.error('[LocationGuard] Release failed:', err);
    }
  }, [doCheckin]);

  const renameLocation = useCallback(async (id, label) => {
    try {
      await sessionService.renameLocation(id, label);
      setLocations((prev) =>
        prev.map((l) => (l.id === id ? { ...l, label } : l)),
      );
    } catch (err) {
      console.error('[LocationGuard] Rename failed:', err);
    }
  }, []);

  const markHome = useCallback(async (id) => {
    try {
      await sessionService.markHome(id);
      setLocations((prev) =>
        prev.map((l) => ({ ...l, isHome: l.id === id })),
      );
    } catch (err) {
      console.error('[LocationGuard] Mark home failed:', err);
    }
  }, []);

  const refreshLocations = useCallback(async () => {
    try {
      const res = await sessionService.getLocations();
      if (!mountedRef.current) return;
      setLocations(res.locations || []);
      setLimit(res.limit || 4);
    } catch {}
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    doCheckin();

    heartbeatRef.current = setInterval(doCheckin, HEARTBEAT_MS);

    return () => {
      mountedRef.current = false;
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    };
  }, [isAuthenticated, doCheckin]);

  return {
    isBlocked,
    locations,
    currentLocation,
    limit,
    blockMessage,
    loading,
    releaseLocation,
    renameLocation,
    markHome,
    refreshLocations,
  };
}
